import {
  useBulkCopyFiles,
  useBulkMoveFiles,
  useBulkMoveToTrash,
  useBulkRestore,
  useBulkToggleFavorite,
  useRequestDownload,
} from '@qse/api'
import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import type { FileItem as UIFileItem } from '@qse/ui'
import { useGlobalFileDownload } from '@qse/ui'
import { useFolderDeletionNotifier } from '@qse/utils'
import * as Sentry from '@sentry/react'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

// Extended FileItem interface that includes isDir property
interface FileItem extends UIFileItem {
  isDir?: boolean
}

/**
 * Sanitize file name for Sentry (remove sensitive paths)
 */
const sanitizeFileName = (fileName: string): string => {
  // Remove full paths, keep only filename
  const parts = fileName.split(/[/\\]/)
  const name = parts[parts.length - 1] || fileName

  // Remove extension if it might contain sensitive info
  const lastDot = name.lastIndexOf('.')

  if (lastDot > 0) {
    return name.substring(0, lastDot) + '[ext]'
  }

  return name
}

export const useFileActions = (externalFiles?: FileItem[]) => {
  const navigate = useNavigate()

  // API hooks
  const bulkToggleFavorite = useBulkToggleFavorite(externalFiles)
  const bulkMoveToTrash = useBulkMoveToTrash()
  const bulkDownload = useRequestDownload()
  const bulkCopyFiles = useBulkCopyFiles()
  const bulkMoveFiles = useBulkMoveFiles()
  const bulkRestore = useBulkRestore()
  const { startRequest, markAvailable, markProcessing, markFailed } = useGlobalFileDownload()

  // Cross-tab communication for folder deletion
  const { notifyFolderDeleted } = useFolderDeletionNotifier()

  // Use external files - no internal data fetching
  const files: FileItem[] = useMemo(() => externalFiles || [], [externalFiles])

  const handleFileClick = useCallback((fileId: number | string) => {
    console.log('fileId', fileId)
    // Single click - just for selection (handled by FileExplorer internally)
  }, [])

  const handleFileDoubleClick = useCallback(
    (fileId: number | string) => {
      // Check if it's a folder and navigate
      const item = files.find(item => item.id === fileId)

      // Check if it's a folder - support both type === 'folder' and isDir === true
      if (item && (item.type === 'folder' || item.isDir === true)) {
        navigate(`/folders/${fileId}`, { state: { folderDetails: item } })
      }
    },
    [files, navigate],
  )

  const handleSelectionChange = useCallback((fileId: number | string, selected: boolean) => {
    console.log('fileId', fileId)
    console.log('selected', selected)
    // Selection changed
  }, [])

  const handleBulkDownload = useCallback(
    async (fileIds: string[]) => {
      try {
        // Process all selected files instead of just the first one
        const downloadPromises = fileIds.map(async fileId => {
          const fileMeta = files.find(f => f.id.toString() === fileId)
          const fileName = fileMeta?.name || 'download'
          const sanitizedName = sanitizeFileName(fileName)

          return Sentry.startSpan(
            {
              op: 'file.download',
              name: `Download ${sanitizedName}`,
            },
            async span => {
              span.setAttribute('file.name', sanitizedName)
              span.setAttribute('file.size', fileMeta?.size || 0)
              span.setAttribute('file.type', fileMeta?.mimeType || 'unknown')

              try {
                // register in tracker immediately
                const trackerId = fileId

                startRequest({
                  id: trackerId,
                  fileId: fileId,
                  name: fileName,
                  mediaType: (fileMeta as any)?.mediaType,
                  mimeType: (fileMeta as any)?.mimeType,
                })
                const downloadResponse = await bulkDownload.mutateAsync({ id: fileId })

                // Check download status
                if (downloadResponse.status === 'AVAILABLE' && downloadResponse.url) {
                  markAvailable(trackerId, downloadResponse.url)
                  span.setStatus({ code: 1 }) // 1 = OK

                  return {
                    success: true,
                    fileId,
                    fileName: files.find(f => f.id.toString() === fileId)?.name,
                  }
                } else if (downloadResponse.status === 'PROCESSING') {
                  markProcessing(trackerId)
                  span.setStatus({ code: 1 }) // 1 = OK
                  span.setAttribute('download.status', 'processing')

                  return {
                    success: true,
                    fileId,
                    status: 'PROCESSING',
                    fileName: files.find(f => f.id.toString() === fileId)?.name,
                  }
                } else {
                  markFailed(trackerId, 'Download not available')
                  span.setStatus({ code: 2 }) // 2 = ERROR
                  span.setAttribute('error', true)

                  return {
                    success: false,
                    fileId,
                    error: 'Download not available',
                    fileName: files.find(f => f.id.toString() === fileId)?.name,
                  }
                }
              } catch (error: any) {
                // Extract error message from backend response
                let errorMessage = 'Failed to download file'

                if (error?.response?.data?.message) {
                  // Backend error message
                  errorMessage = error.response.data.message
                } else if (error?.message) {
                  // Generic error message
                  errorMessage = error.message
                } else if (error?.response?.status === 403) {
                  errorMessage = 'You do not have permission to download this file'
                } else if (error?.response?.status === 404) {
                  errorMessage = 'File not found'
                } else if (error?.response?.status >= 500) {
                  errorMessage = 'Server error occurred while downloading file'
                }

                span.setStatus({ code: 2 }) // 2 = ERROR
                span.setAttribute('error', true)
                markFailed(fileId, errorMessage)

                return {
                  success: false,
                  fileId,
                  error: errorMessage,
                  fileName: files.find(f => f.id.toString() === fileId)?.name,
                }
              }
            },
          )
        })

        const results = await Promise.allSettled(downloadPromises)
        const successfulDownloads = results
          .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
          .map(result => result.value)
          .filter(result => result.success)

        const failedDownloads = results
          .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
          .map(result => result.value)
          .filter(result => !result.success)

        const processingDownloads = successfulDownloads.filter(
          result => result.status === 'PROCESSING',
        )

        console.log('successfulDownloads', successfulDownloads)
        console.log('failedDownloads', failedDownloads)
        console.log('processingDownloads', processingDownloads)

        // No toasts; tracker will reflect status // TODO: check if we need to show toasts in other place than the bottom right corner
      } catch (error: any) {
        // Extract error message from backend response
        let errorMessage = 'Failed to download files'

        if (error?.response?.data?.message) {
          // Backend error message
          errorMessage = error.response.data.message
        } else if (error?.message) {
          // Generic error message
          errorMessage = error.message
        } else if (error?.response?.status === 403) {
          errorMessage = 'You do not have permission to download these files'
        } else if (error?.response?.status === 404) {
          errorMessage = 'Files not found'
        } else if (error?.response?.status >= 500) {
          errorMessage = 'Server error occurred while downloading files'
        }

        toast.error(errorMessage)
        throw error
      }
    },
    [bulkDownload, files, markAvailable, markFailed, markProcessing, startRequest],
  )

  const handleRename = useCallback(
    async (fileIds: string[], path: string) => {
      const selectedFileIds = fileIds.map(id => id)
      const firstFileId = selectedFileIds[0]
      const firstFile = files.find(file => file.id === firstFileId)
      const currentFileName = firstFile?.name || ''
      const parentId = firstFile?.parentId

      const config = {
        modalHeader: {
          title: 'Rename',
          showCloseButton: true,
        },
        data: {
          fileIds: fileIds,
          currentFileName: currentFileName,
          path: path,
          parentId: parentId,
        },
        actions: [
          {
            label: 'Cancel',
            variant: 'flat',
          },
          {
            label: 'Save',
            color: 'primary',
            type: 'submit',
            onPress: async (values: any) => {
              // The form submission is handled by Formik's onSubmit
              // This onPress is just for the button action, not for API calls
              console.log('Action button pressed with values:', values)
            },
          },
        ],
      }

      useActiveModelStore.setState({
        data: config,
        width: 'max-w-[490px]',
        modelActive: ModelControlEnum.RENAME_FILE,
        toggleModel: true,
        scrollBehavior: 'inside',
      })
    },
    [files],
  )

  const handleBulkToggleFavorite = useCallback(
    async (fileIds: string[]) => {
      try {
        await bulkToggleFavorite.mutateAsync({ fileIds })
      } catch (error: any) {
        // Extract error message from backend response
        let errorMessage = 'Failed to update favorite status'

        if (error?.response?.data?.message) {
          // Backend error message
          errorMessage = error.response.data.message
        } else if (error?.message) {
          // Generic error message
          errorMessage = error.message
        } else if (error?.response?.status === 403) {
          errorMessage = 'You do not have permission to update favorite status'
        } else if (error?.response?.status === 404) {
          errorMessage = 'One or more files not found'
        } else if (error?.response?.status >= 500) {
          errorMessage = 'Server error occurred while updating favorite status'
        }

        toast.error(errorMessage)
        throw error
      }
    },
    [bulkToggleFavorite],
  )

  const handleBulkCopy = useCallback(
    async (fileIds: string[]) => {
      try {
        // Get the files being copied for better feedback
        const filesToCopy = files.filter(file => fileIds.includes(file.id.toString()))

        // Check if any of the selected files are folders
        const foldersToCopy = filesToCopy.filter(
          file => file.type === 'folder' || file.isDir === true,
        )

        if (foldersToCopy.length > 0) {
          // Show custom error message for folders
          if (foldersToCopy.length === 1) {
            toast.error(
              `Cannot copy folder "${foldersToCopy[0].name}". Folder copying is not supported.`,
            )
          } else {
            const folderNames = foldersToCopy.slice(0, 3).map(f => f.name)
            const remaining = foldersToCopy.length - 3
            const folderList =
              folderNames.join(', ') + (remaining > 0 ? ` and ${remaining} more` : '')

            toast.error(`Cannot copy folders: ${folderList}. Folder copying is not supported.`)
          }

          return // Exit early without making API call
        }

        // Create descriptive message for loading state
        let copyMessage = ''

        if (filesToCopy.length === 1) {
          copyMessage = `Copying "${filesToCopy[0].name}"`
        } else if (filesToCopy.length > 1) {
          const fileNames = filesToCopy.slice(0, 3).map(f => f.name)
          const remaining = filesToCopy.length - 3

          copyMessage = `Copying ${filesToCopy.length} items: ${fileNames.join(', ')}${remaining > 0 ? ` and ${remaining} more` : ''}`
        } else {
          // Fallback if files array is empty or doesn't contain the files
          copyMessage = `Copying ${fileIds.length} item${fileIds.length === 1 ? '' : 's'}`
        }

        // Show loading toast
        const loadingToast = toast.loading(copyMessage)

        // Execute the copy operation
        await bulkCopyFiles.mutateAsync({ fileIds })

        // Dismiss loading toast
        toast.dismiss(loadingToast)
      } catch (error: any) {
        // Dismiss loading toast on error
        toast.dismiss()

        // Get file names for error message
        const filesToCopy = files.filter(file => fileIds.includes(file.id.toString()))

        // Extract error message from backend response
        let errorMessage = 'Failed to copy files'

        if (error?.response?.data?.message) {
          // Backend error message
          errorMessage = error.response.data.message
        } else if (error?.message) {
          // Generic error message
          errorMessage = error.message
        } else if (error?.response?.status === 409) {
          errorMessage = 'File already exists in the destination'
        } else if (error?.response?.status === 403) {
          errorMessage = 'You do not have permission to copy these files'
        } else if (error?.response?.status === 404) {
          errorMessage = 'One or more files not found'
        } else if (error?.response?.status >= 500) {
          errorMessage = 'Server error occurred while copying files'
        }

        // Create specific error message based on number of files
        const specificMessage =
          filesToCopy.length === 1
            ? `Failed to copy "${filesToCopy[0].name}": ${errorMessage}`
            : `Failed to copy ${fileIds.length} items: ${errorMessage}`

        toast.error(specificMessage)

        // Re-throw the error so the calling code knows the operation failed
        throw error
      }
    },
    [bulkCopyFiles, files],
  )

  const handleBulkMove = useCallback(
    async (fileIds: string[], targetParentId?: string, targetParentPath?: string) => {
      try {
        // Get the files being moved for better feedback
        const filesToMove = files.filter(file => fileIds.includes(file.id.toString()))
        const targetFolder = files.find(file => file.id.toString() === targetParentId)

        // Create descriptive message for loading state
        let moveMessage = ''

        if (filesToMove.length === 1) {
          moveMessage = `Moving "${filesToMove[0].name}"`
        } else if (filesToMove.length > 1) {
          const fileNames = filesToMove.slice(0, 3).map(f => f.name)
          const remaining = filesToMove.length - 3

          moveMessage = `Moving ${filesToMove.length} items: ${fileNames.join(', ')}${remaining > 0 ? ` and ${remaining} more` : ''}`
        } else {
          // Fallback if files array is empty or doesn't contain the files
          moveMessage = `Moving ${fileIds.length} item${fileIds.length === 1 ? '' : 's'}`
        }

        if (targetFolder) {
          moveMessage += ` to "${targetFolder.name}"`
        }

        // Show loading toast
        const loadingToast = toast.loading(moveMessage)

        // Execute the mutation - the onSuccess callback in the mutation will handle the response and UI updates
        await bulkMoveFiles.mutateAsync({
          fileIds,
          targetParentId,
          targetParentPath,
        })

        // Dismiss loading toast - the onSuccess callback will show the appropriate success/error message
        toast.dismiss(loadingToast)
      } catch (error: any) {
        // Dismiss loading toast on error
        toast.dismiss()

        // Get file names for error message
        const filesToMove = files.filter(file => fileIds.includes(file.id.toString()))

        // Extract error message from backend response
        let errorMessage = 'Failed to move files'

        if (error?.response?.data?.message) {
          // Backend error message
          errorMessage = error.response.data.message
        } else if (error?.message) {
          // Generic error message
          errorMessage = error.message
        } else if (error?.response?.status === 409) {
          errorMessage = 'File already exists in the destination'
        } else if (error?.response?.status === 403) {
          errorMessage = 'You do not have permission to move these files'
        } else if (error?.response?.status === 404) {
          errorMessage = 'One or more files not found'
        } else if (error?.response?.status >= 500) {
          errorMessage = 'Server error occurred while moving files'
        }

        // Create specific error message based on number of files
        const specificMessage =
          filesToMove.length === 1
            ? `Failed to move "${filesToMove[0].name}": ${errorMessage}`
            : `Failed to move ${fileIds.length} items: ${errorMessage}`

        toast.error(specificMessage)

        // Re-throw the error so the calling code knows the operation failed
        throw error
      }
    },
    [bulkMoveFiles, files],
  )

  const handleBulkDelete = useCallback(
    async (fileIds: string[], onSuccess?: () => void) => {
      // Get the files being deleted for better feedback
      const filesToDelete = files.filter(file => fileIds.includes(file.id.toString()))

      // Create dynamic title and message based on number of items
      let title: string
      let message: string

      if (fileIds.length === 1) {
        const fileName = filesToDelete[0]?.name || 'this item'

        title = 'Move to Trash?'
        message = `Are you sure you want to move "${fileName}" to the trash? You can restore it later.`
      } else {
        title = 'Move to Trash?'
        message = `Are you sure you want to move ${fileIds.length} items to the trash? You can restore them later.`
      }

      const config = {
        modalHeader: {
          title: 'Confirmation',
          showCloseButton: true,
        },

        title,
        message,
        icon: '/assets/svg/delete.svg',
        actions: [
          {
            label: 'Cancel',
            variant: 'flat',
          },
          {
            label: 'Move to Trash',
            color: 'primary',
            onPress: async () => {
              try {
                await bulkMoveToTrash.mutateAsync({ fileIds })

                // Notify other tabs about folder deletions
                const foldersToDelete = filesToDelete.filter(
                  file => file.type === 'folder' || file.isDir === true,
                )

                foldersToDelete.forEach(folder => {
                  notifyFolderDeleted(folder.id.toString(), folder.name, folder.parentId)
                })

                onSuccess?.()
              } catch (error: any) {
                // Extract error message from backend response
                let errorMessage = 'Failed to move files to trash'

                if (error?.response?.data?.message) {
                  // Backend error message
                  errorMessage = error.response.data.message
                } else if (error?.message) {
                  // Generic error message
                  errorMessage = error.message
                } else if (error?.response?.status === 403) {
                  errorMessage = 'You do not have permission to move these files to trash'
                } else if (error?.response?.status === 404) {
                  errorMessage = 'One or more files not found'
                } else if (error?.response?.status >= 500) {
                  errorMessage = 'Server error occurred while moving files to trash'
                }

                toast.error(errorMessage)
              }
            },
          },
        ],
      }

      useActiveModelStore.setState({
        data: config,
        width: 'max-w-[660px]',
        modelActive: ModelControlEnum.CONFIRMATION,
        toggleModel: true,
        scrollBehavior: 'inside',
      })
    },
    [bulkMoveToTrash, files, notifyFolderDeleted],
  )

  const handleBulkRestore = useCallback(
    async (fileIds: string[]) => {
      const config = {
        modalHeader: {
          title: 'Confirmation',
          showCloseButton: true,
        },

        title: 'Restore Files?',
        message: 'Are you sure you want to restore these files from the trash?',
        icon: '/assets/svg/restore.svg',
        actions: [
          {
            label: 'Cancel',
            variant: 'flat',
          },
          {
            label: 'Restore',
            color: 'primary',
            onPress: async () => {
              try {
                await bulkRestore.mutateAsync({ fileIds })
                toast.success('Files restored from trash')
              } catch (error: any) {
                // Extract error message from backend response
                let errorMessage = 'Failed to restore files from trash'

                if (error?.response?.data?.message) {
                  // Backend error message
                  errorMessage = error.response.data.message
                } else if (error?.message) {
                  // Generic error message
                  errorMessage = error.message
                } else if (error?.response?.status === 403) {
                  errorMessage = 'You do not have permission to restore these files'
                } else if (error?.response?.status === 404) {
                  errorMessage = 'One or more files not found in trash'
                } else if (error?.response?.status >= 500) {
                  errorMessage = 'Server error occurred while restoring files'
                }

                toast.error(errorMessage)
              }
            },
          },
        ],
      }

      useActiveModelStore.setState({
        data: config,
        width: 'max-w-[660px]',
        modelActive: ModelControlEnum.CONFIRMATION,
        toggleModel: true,
        scrollBehavior: 'inside',
      })
    },
    [bulkRestore],
  )

  return {
    // Data
    files,
    isLoading: false, // No longer fetching data, so isLoading is false
    error: null, // No longer fetching data, so error is null

    // Actions
    handleFileClick,
    handleFileDoubleClick,
    handleSelectionChange,
    handleBulkDownload,
    handleRename,
    handleBulkToggleFavorite,
    handleBulkCopy,
    handleBulkMove,
    handleBulkDelete,
    handleBulkRestore,
    // Utilities
    // refetch: () => {}, // No longer refetching data, so refetch is a no-op
  }
}
